// Pure helpers of the administration panel (no DOM): grants summaries and validation, user form
// validation and patches, floor/department/agent validation, reordering, « Données de l’étage »
// (a floor using another floor's data) and a department changing floor, connector and engine
// status. Tested in test/ui-admin.test.js.
import { t } from '../../i18n/fr.js';
import { sortedFloors, floorDepartments } from '../../store.js';
import { PALETTE } from '../../format.js';
import { normalizePolicy, samePolicy } from '../connectorpolicy.js';
import './strings.js';

export {
  POLICY_MODES, normalizePolicy, samePolicy, policyRestricts, policyAllows, policyChoices, policySummary, policyNames, keyLabel,
} from '../connectorpolicy.js';

/** Same rule as the server (server/modules/auth.js USERNAME_RE). */
export const USERNAME_RE = /^[a-z0-9._-]{2,32}$/;
/** An office id (floor, department…), same rule as the server (server/office/validate.js ID_RE). */
export const ID_RE = /^[A-Za-z0-9_-]{1,64}$/;
/** Grant levels, lowest first (SPEC §8). */
export const GRANT_LEVELS = ['view', 'operate', 'manage'];
/** Field limits (SPEC §7 and §13; names and descriptions have UI caps). */
export const LIMITS = Object.freeze({
  displayName: 60,
  floorName: 60,
  floorDescription: 300,
  floorContext: 8000,
  deptName: 60,
  deptContext: 4000,
  agentName: 22,
  agentRole: 80,
  agentDoes: 300,
});
/** Engines an agent can use (SPEC §13). */
export const AGENT_ENGINES = ['claude', 'codex', 'openai-image', 'gemini', 'kie'];
/** Palette keys usable as floor/department colors. */
export const COLORS = PALETTE;

// eslint-disable-next-line no-control-regex
const CONTROL_RE = /[\u0000-\u001f\u007f]/g;

function str(v) {
  return typeof v === 'string' ? v : v == null ? '' : String(v);
}

/** Trimmed single-line text without control characters. */
export function cleanLine(v) {
  return str(v).replace(CONTROL_RE, ' ').replace(/\s+/g, ' ').trim();
}

/** Lower-cased, trimmed username. */
export function normalizeUsername(v) {
  return str(v).trim().toLowerCase();
}

/** Label of a grant level ("Voir", "Utiliser", "Gérer"; admin → "Tout"). */
export function levelLabel(level) {
  return ['view', 'operate', 'manage', 'admin'].includes(level) ? t(`adm.level.${level}`) : str(level);
}

// ---------------------------------------------------------------------------------------------
// Grants

/**
 * Describes each grant with display names (unknown floors/departments are named by id).
 * @param {Array<{floorId: string, deptId: string, level: string}>} grants
 * @param {{floors?: object[], departments?: object[]}} office
 * @returns {Array<{floorId, deptId, level, floor: string, dept: string, levelLabel: string, text: string}>}
 */
export function grantLines(grants, office = {}) {
  const floors = office.floors || [];
  const depts = office.departments || [];
  return (Array.isArray(grants) ? grants : []).filter((g) => g && typeof g === 'object').map((g) => {
    const f = floors.find((x) => x.id === g.floorId);
    const d = g.deptId === '*' ? null : depts.find((x) => x.id === g.deptId);
    const floor = f ? f.name : t('adm.grants.unknownFloor', { id: str(g.floorId) });
    const dept = g.deptId === '*' ? t('adm.grants.allFloor') : d ? d.name : t('adm.grants.unknownDept', { id: str(g.deptId) });
    const lvl = levelLabel(g.level);
    return { floorId: g.floorId, deptId: g.deptId, level: g.level, floor, dept, levelLabel: lvl, text: t('adm.grants.item', { floor, dept, level: lvl }) };
  });
}

/**
 * One-line summary of what a user can reach: "Tout (administrateur)", "Aucun accès",
 * "Boutique · tout l’étage (Utiliser) ; Ads · Créa (Voir) +2".
 * @param {{role?: string, grants?: object[]}} user
 * @param {object} office
 * @param {{max?: number}} [opts] number of grants spelled out before "+n"
 */
export function grantsSummary(user, office = {}, { max = 2 } = {}) {
  if (user?.role === 'admin') return t('adm.grants.admin');
  const lines = grantLines(user?.grants, office);
  if (!lines.length) return t('adm.grants.none');
  const shown = lines.slice(0, Math.max(1, max)).map((l) => l.text).join(' ; ');
  const rest = lines.length - Math.max(1, max);
  return rest > 0 ? `${shown} ${t('adm.grants.more', { n: rest })}` : shown;
}

/**
 * Validates grants against the office (French messages, one per problem; empty when valid).
 * @param {Array<{floorId, deptId, level}>} grants
 * @param {{floors?: object[], departments?: object[]}} office
 * @returns {string[]}
 */
export function validateGrants(grants, office = {}) {
  const problems = [];
  const seen = new Set();
  const floors = office.floors || [];
  const depts = office.departments || [];
  (Array.isArray(grants) ? grants : []).forEach((g, i) => {
    const n = i + 1;
    if (!g || !g.floorId) { problems.push(t('adm.grant.err.floor', { n })); return; }
    if (!floors.some((f) => f.id === g.floorId)) { problems.push(t('adm.grant.err.unknownFloor', { n })); return; }
    if (g.deptId !== '*' && !depts.some((d) => d.id === g.deptId && d.floorId === g.floorId)) {
      problems.push(t('adm.grant.err.dept', { n }));
      return;
    }
    if (!GRANT_LEVELS.includes(g.level)) { problems.push(t('adm.grant.err.level', { n })); return; }
    const key = `${g.floorId}\u0000${g.deptId}`;
    if (seen.has(key)) problems.push(t('adm.grant.err.duplicate', { n }));
    seen.add(key);
  });
  return problems;
}

/** Grants reduced to plain {floorId, deptId, level} objects (what the API takes). */
export function cleanGrants(grants) {
  return (Array.isArray(grants) ? grants : []).filter(Boolean)
    .map((g) => ({ floorId: str(g.floorId), deptId: g.deptId === '*' || !g.deptId ? '*' : str(g.deptId), level: str(g.level) }));
}

/** True when two grant lists hold the same grants (order-insensitive). */
export function sameGrants(a, b) {
  const key = (list) => cleanGrants(list).map((g) => `${g.floorId}\u0000${g.deptId}\u0000${g.level}`).sort().join('\n');
  return key(a) === key(b);
}

/** A new grant row for the editor: the first floor, whole floor, "view". */
export function defaultGrant(office = {}) {
  const f = sortedFloors(office.floors || [])[0];
  return { floorId: f ? f.id : '', deptId: '*', level: 'view' };
}

/** Department options of a floor for the grants editor: "Tout l’étage" (*) first. */
export function deptOptions(office = {}, floorId) {
  return [{ value: '*', label: t('adm.grant.allFloor') },
    ...floorDepartments(office.departments || [], floorId).map((d) => ({ value: d.id, label: d.name }))];
}

// ---------------------------------------------------------------------------------------------
// Users

/**
 * Validates the user form. `personalConnectors` (« Peut ajouter des connecteurs »: the member may
 * add connectors to the office's library; the server also reads it as `canAddConnectors`) is kept
 * only for a member and only when given as a boolean: an administrator always has it. Likewise
 * `connectorPolicy` (« Connecteurs autorisés »), normalised, only for a member: an administrator
 * is never restricted.
 * @param {{username?: string, displayName?: string, role?: string, grants?: object[], personalConnectors?: boolean,
 *   connectorPolicy?: {mode: string, keys?: string[]}}} input
 * @param {{isNew?: boolean, office?: object}} [opts]
 * @returns {{ok: boolean, value: object, errors: {username?: string, displayName?: string, role?: string, grants?: string[]}}}
 */
export function validateUser(input = {}, { isNew = true, office = {} } = {}) {
  const errors = {};
  const value = {};
  if (isNew) {
    value.username = normalizeUsername(input.username);
    if (!value.username) errors.username = t('adm.err.required');
    else if (!USERNAME_RE.test(value.username)) errors.username = t('adm.err.username');
  }
  value.displayName = cleanLine(input.displayName);
  if (value.displayName.length > LIMITS.displayName) errors.displayName = t('adm.err.tooLong', { max: LIMITS.displayName });
  value.role = input.role === 'admin' ? 'admin' : 'member';
  value.grants = value.role === 'admin' ? [] : cleanGrants(input.grants);
  if (value.role !== 'admin' && typeof input.personalConnectors === 'boolean') value.personalConnectors = input.personalConnectors;
  if (value.role !== 'admin' && input.connectorPolicy !== undefined) value.connectorPolicy = normalizePolicy(input.connectorPolicy);
  if (value.role !== 'admin') {
    const problems = validateGrants(value.grants, office);
    if (problems.length) errors.grants = problems;
  }
  return { ok: Object.keys(errors).length === 0, value, errors };
}

/**
 * The PATCH body for an edited user: only the fields that changed (grants compared as sets;
 * grants are not sent for admins). `personalConnectors` is a member's: never sent for an
 * administrator (always allowed), and always sent with a demotion to member (the server shows an
 * administrator's permission as true, whatever is stored). `connectorPolicy` follows the same rule
 * (an administrator shows « Tous » whatever is stored, so a demotion always says what applies).
 * @param {object} original PublicUser
 * @param {{displayName?: string, role?: string, disabled?: boolean, grants?: object[], personalConnectors?: boolean,
 *   connectorPolicy?: {mode: string, keys?: string[]}}} edited
 * @returns {object}
 */
export function userPatch(original = {}, edited = {}) {
  const patch = {};
  const displayName = cleanLine(edited.displayName);
  if (edited.displayName !== undefined && displayName !== cleanLine(original.displayName || original.username)) {
    patch.displayName = displayName;
  }
  if (edited.role && edited.role !== original.role) patch.role = edited.role;
  if (edited.disabled !== undefined && !!edited.disabled !== !!original.disabled) patch.disabled = !!edited.disabled;
  const role = patch.role || original.role;
  if (role !== 'admin' && edited.grants !== undefined && !sameGrants(edited.grants, original.grants)) {
    patch.grants = cleanGrants(edited.grants);
  }
  if (role !== 'admin' && typeof edited.personalConnectors === 'boolean'
    && (patch.role === 'member' || edited.personalConnectors !== (original.personalConnectors === true))) {
    patch.personalConnectors = edited.personalConnectors;
  }
  if (role !== 'admin' && edited.connectorPolicy !== undefined && edited.connectorPolicy !== null
    && (patch.role === 'member' || !samePolicy(edited.connectorPolicy, original.connectorPolicy))) {
    patch.connectorPolicy = normalizePolicy(edited.connectorPolicy);
  }
  return patch;
}

/**
 * What an admin may do to a user row (the server refuses the same things; SPEC §7).
 * @param {object|null} me the logged-in user
 * @param {object} user the edited user
 */
export function selfGuards(me, user) {
  const isSelf = !!(me && user && me.id === user.id);
  return { isSelf, canDemote: !isSelf, canDisable: !isSelf, canReset: !isSelf, canDelete: !isSelf };
}

/**
 * Account status badge: disabled > temporary password > active.
 * @returns {{key: 'disabled'|'mustChange'|'active', label: string, tone: string}}
 */
export function userStatus(user = {}) {
  if (user.disabled) return { key: 'disabled', label: t('adm.status.disabled'), tone: 'muted' };
  if (user.mustChangePassword) return { key: 'mustChange', label: t('adm.status.mustChange'), tone: 'amber' };
  return { key: 'active', label: t('adm.status.active'), tone: 'success' };
}

/** Users sorted: admins first, then by display name. */
export function sortUsers(users) {
  return [...(Array.isArray(users) ? users : [])].sort((a, b) => Number(b.role === 'admin') - Number(a.role === 'admin')
    || str(a.displayName || a.username).localeCompare(str(b.displayName || b.username), 'fr'));
}

/** Users whose username or display name contains `q` (case- and accent-insensitive). */
export function filterUsers(users, q) {
  const needle = fold(q);
  if (!needle) return Array.isArray(users) ? users : [];
  return (users || []).filter((u) => fold(`${u.username} ${u.displayName || ''}`).includes(needle));
}

/** Lower case without accents (for searches). */
export function fold(s) {
  return str(s).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
}

// ---------------------------------------------------------------------------------------------
// Floors, departments, agents

function checkText(errors, key, value, { required = false, max }) {
  if (required && !value) errors[key] = t('adm.err.required');
  else if (max && value.length > max) errors[key] = t('adm.err.tooLong', { max });
}

/** A palette key or a #rrggbb color (the server's rule); '' when invalid. */
export function cleanColor(c) {
  const s = str(c).trim();
  if (COLORS.includes(s)) return s;
  return /^#[0-9a-f]{6}$/i.test(s) ? s.toLowerCase() : '';
}

/**
 * Validates the floor form (only these fields: a floor read from an older office may still carry
 * `personalConnectors`, `tablesFloors` or `studioFloor`, no longer floor settings, and they are
 * never sent back). `studio`: the floor's studio, for every one of its services (off unless true).
 * `chicken`: « La poule de l’étage », a chicken strolling in the floor's 3D view (off unless true).
 * `cat`: « Le chat de l’étage », a grey and white cat living in the floor's 3D view (off unless true).
 * `dataFloor`: « Données de l’étage », the floor whose data this one uses (its host), null for its
 * own ('' and a missing value too); only its format here, as on the server (the office service
 * checks the host). The order is not a field of the form (the up / down arrows set it).
 * @param {{name?, description?, color?, context?, connectors?: string[], studio?: boolean, chicken?: boolean,
 *   cat?: boolean, dataFloor?: string|null}} input
 * @returns {{ok: boolean, value: object, errors: object}}
 */
export function validateFloor(input = {}) {
  const errors = {};
  const value = {
    name: cleanLine(input.name),
    description: cleanLine(input.description),
    color: cleanColor(input.color),
    context: str(input.context).replace(/\r\n/g, '\n').trim(),
    connectors: [...new Set((Array.isArray(input.connectors) ? input.connectors : []).map(str).filter(Boolean))],
    studio: input.studio === true,
    chicken: input.chicken === true,
    cat: input.cat === true,
    dataFloor: str(input.dataFloor).trim() || null,
  };
  checkText(errors, 'name', value.name, { required: true, max: LIMITS.floorName });
  checkText(errors, 'description', value.description, { max: LIMITS.floorDescription });
  checkText(errors, 'context', value.context, { max: LIMITS.floorContext });
  if (!value.color) errors.color = t('adm.err.color');
  if (value.dataFloor !== null && !ID_RE.test(value.dataFloor)) errors.dataFloor = t('adm.err.dataFloor');
  return { ok: Object.keys(errors).length === 0, value, errors };
}

/**
 * Validates the department form.
 * @param {{name?, color?, context?, leadAgentId?}} input
 */
export function validateDept(input = {}) {
  const errors = {};
  const value = {
    name: cleanLine(input.name),
    color: cleanColor(input.color),
    context: str(input.context).replace(/\r\n/g, '\n').trim(),
  };
  if (input.leadAgentId !== undefined && input.leadAgentId !== null && input.leadAgentId !== '') value.leadAgentId = str(input.leadAgentId);
  checkText(errors, 'name', value.name, { required: true, max: LIMITS.deptName });
  checkText(errors, 'context', value.context, { max: LIMITS.deptContext });
  if (!value.color) errors.color = t('adm.err.color');
  return { ok: Object.keys(errors).length === 0, value, errors };
}

/**
 * Validates the "new agent" form (name upper-cased).
 * @param {{name?, role?, does?, engine?}} input
 */
export function validateAgent(input = {}) {
  const errors = {};
  const value = {
    name: cleanLine(input.name).toUpperCase(),
    role: cleanLine(input.role),
    does: str(input.does).replace(CONTROL_RE, ' ').replace(/\s+/g, ' ').trim(),
    engine: AGENT_ENGINES.includes(input.engine) ? input.engine : 'claude',
  };
  if (!value.name) errors.name = t('adm.err.required');
  else if (value.name.length > LIMITS.agentName) errors.name = t('adm.err.agentName');
  checkText(errors, 'role', value.role, { required: true, max: LIMITS.agentRole });
  checkText(errors, 'does', value.does, { max: LIMITS.agentDoes });
  return { ok: Object.keys(errors).length === 0, value, errors };
}

/**
 * Only the fields of `value` that differ from `original` (strings compared as-is, arrays as sets,
 * booleans with a missing original read as false: a switch left off on a record without the field
 * sends nothing).
 * @param {object} original
 * @param {object} value
 */
export function changedFields(original = {}, value = {}) {
  const out = {};
  for (const [k, v] of Object.entries(value)) {
    const o = original[k];
    if (typeof v === 'boolean') {
      if (v !== (o === true)) out[k] = v;
    } else if (Array.isArray(v)) {
      const a = [...v].map(str).sort().join('\n');
      const b = [...(Array.isArray(o) ? o : [])].map(str).sort().join('\n');
      if (a !== b) out[k] = v;
    } else if (str(v) !== str(o ?? '')) {
      out[k] = v;
    }
  }
  return out;
}

/**
 * Moves an item by `delta` positions in a list ordered by `order` (ties by name) and returns the
 * PATCHes needed: every item whose position-based order (0..n-1) differs from its current one.
 * @param {Array<{id: string, order?: number, name?: string}>} items
 * @param {string} id
 * @param {number} delta −1 (up) or +1 (down)
 * @returns {Array<{id: string, order: number}>} empty when the move is impossible
 */
export function reorderPatches(items, id, delta) {
  const list = [...(items || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || str(a.name).localeCompare(str(b.name), 'fr'));
  const from = list.findIndex((x) => x.id === id);
  const to = from + Math.sign(delta || 0);
  if (from < 0 || to < 0 || to >= list.length || to === from) return [];
  const [moved] = list.splice(from, 1);
  list.splice(to, 0, moved);
  return list.map((x, i) => ({ id: x.id, order: i, prev: x.order })).filter((x) => x.prev !== x.order).map(({ id: itemId, order }) => ({ id: itemId, order }));
}

/**
 * The order of a department's agents after moving one by `delta` (PUT
 * /api/departments/:id/agent-order). The lead stays first, as on the office plan where it has the
 * desk facing the team: it does not move and no agent can pass it.
 * @param {Array<{id: string, lead?: boolean}>} agents the department's agents as shown (deptAgents: lead first)
 * @param {string} id
 * @param {number} delta −1 (up) or +1 (down)
 * @returns {string[]|null} every agent id in the new order, null when the move is impossible
 */
export function agentOrderAfterMove(agents, id, delta) {
  const list = [...(agents || [])];
  const from = list.findIndex((a) => a.id === id);
  const to = from + Math.sign(delta || 0);
  if (from < 0 || to === from || to < 0 || to >= list.length) return null;
  if (list[from].lead || list[to].lead) return null;
  const [moved] = list.splice(from, 1);
  list.splice(to, 0, moved);
  return list.map((a) => a.id);
}

/**
 * office.agents with the agents of `deptId` put back in their own slots in the order of `ids`, as
 * the server stores it (the other departments do not move). Unknown or missing ids → unchanged.
 * @param {object[]} agents office.agents
 * @param {string} deptId
 * @param {string[]} ids every agent id of the department
 * @returns {object[]} a new array
 */
export function withAgentOrder(agents, deptId, ids) {
  const list = [...(agents || [])];
  const slots = [];
  list.forEach((a, i) => { if (a.deptId === deptId) slots.push(i); });
  const byId = new Map(slots.map((i) => [list[i].id, list[i]]));
  if (!Array.isArray(ids) || ids.length !== slots.length || new Set(ids).size !== ids.length || !ids.every((x) => byId.has(x))) return list;
  slots.forEach((slot, i) => { list[slot] = byId.get(ids[i]); });
  return list;
}

/** Counts for a floor card: departments and agents on it. */
export function floorCounts(office = {}, floorId) {
  const depts = (office.departments || []).filter((d) => d.floorId === floorId);
  const ids = new Set(depts.map((d) => d.id));
  const agents = (office.agents || []).filter((a) => a.floorId === floorId || ids.has(a.deptId));
  return { depts: depts.length, agents: agents.length };
}

/** The lead agent of a department (leadAgentId, else the agent flagged lead), or null. */
export function deptLead(office = {}, dept = {}) {
  const agents = (office.agents || []).filter((a) => a.deptId === dept.id);
  return agents.find((a) => a.id === dept.leadAgentId) || agents.find((a) => a.lead) || null;
}

// ---------------------------------------------------------------------------------------------
// Données de l’étage (`floor.dataFloor`) and a department changing floor

function isGuestValue(v) {
  return typeof v === 'string' && v !== '';
}

/**
 * The floor whose data a floor uses (the same rule as server/office/datafloor.js): its `dataFloor`
 * when it names another floor of the list that uses no other floor's data itself, else the floor
 * itself (a stale or chained value, from a hand-edited office file, is ignored by the server too).
 * @param {object[]} floors
 * @param {string} id
 * @returns {string}
 */
export function dataHostOf(floors, id) {
  const list = Array.isArray(floors) ? floors : [];
  const target = list.find((f) => f?.id === id)?.dataFloor;
  if (!isGuestValue(target) || target === id) return id;
  const host = list.find((f) => f?.id === target);
  return host && !isGuestValue(host.dataFloor) ? host.id : id;
}

/** Do two floors use the same data (the same host, or the same floor)? */
export function sameDataFloors(floors, a, b) {
  return dataHostOf(floors, a) === dataHostOf(floors, b);
}

/**
 * The floors that use this floor's data, in the elevator order. `stored`: every floor naming it
 * as its `dataFloor`, even a value the server ignores (what the server counts before letting a
 * floor use another floor's data); else only those that really use it.
 * @param {object[]} floors
 * @param {string} id
 * @param {{stored?: boolean}} [opts]
 * @returns {object[]}
 */
export function dataGuestsOf(floors, id, { stored = false } = {}) {
  const list = Array.isArray(floors) ? floors : [];
  return sortedFloors(list).filter((f) => f.id !== id && (stored ? f.dataFloor === id : dataHostOf(list, f.id) === id));
}

/**
 * The choices of the « Données de l’étage » select of a floor (null: a new floor), as
 * [value, label]: « Les siennes » (''), then every other floor that uses its own data, in the
 * elevator order (a floor that uses another floor's data cannot lend its own: one level only). A
 * stored value that is no longer valid stays listed, marked as ignored, so that a form saved
 * without touching it sends nothing.
 * @param {object[]} floors
 * @param {object|null} floor
 * @returns {Array<[string, string]>}
 */
export function dataFloorChoices(floors, floor) {
  const list = Array.isArray(floors) ? floors : [];
  const own = floor?.id;
  const out = [['', t('adm.floor.dataOwn')]];
  for (const f of sortedFloors(list)) {
    if (f.id !== own && !isGuestValue(f.dataFloor)) out.push([f.id, str(f.name || f.id)]);
  }
  const current = floor?.dataFloor;
  if (isGuestValue(current) && !out.some(([v]) => v === current)) {
    const name = list.find((f) => f.id === current)?.name;
    out.push([current, t('adm.floor.dataIgnored', { name: str(name || current) })]);
  }
  return out;
}

/**
 * The floors a department may move to (POST /api/departments/:id/move): every other floor, in
 * the elevator order.
 * @param {{floors?: object[]}} office
 * @param {{floorId?: string}} dept
 * @returns {object[]}
 */
export function moveTargets(office = {}, dept = {}) {
  return sortedFloors(office.floors || []).filter((f) => f.id !== dept.floorId);
}

/** « a, b et c ». */
function listText(items) {
  const list = items.filter(Boolean);
  return list.length > 1 ? `${list.slice(0, -1).join(', ')} ${t('adm.move.and')} ${list[list.length - 1]}` : (list[0] || '');
}

function howMany(v) {
  return Array.isArray(v) ? v.length : Number.isFinite(Number(v)) ? Number(v) : 0;
}

/**
 * The text of the confirmation of a department's move, from the server's plan (the answer of a
 * dry run): what moves with it, the grants updated, what to know (the server's warnings, which
 * name the people whose access changes: `grants.extended`, `gained`, `lost`). « Aucun accès… »
 * only when nobody's grants nor access change.
 * @param {{department?: {name?: string}, from?: {name?: string}, to?: {name?: string}, agents?: string[],
 *   routines?: string[], triggers?: string[], tasks?: number,
 *   grants?: {users?: string[], gained?: object[], lost?: object[]}, warnings?: string[]}} plan
 * @returns {string}
 */
export function moveSummary(plan = {}) {
  const p = plan && typeof plan === 'object' ? plan : {};
  const routines = howMany(p.routines);
  const triggers = howMany(p.triggers);
  const tasks = howMany(p.tasks);
  const what = [
    t('adm.move.agents', { n: howMany(p.agents) }),
    routines ? t('adm.move.routines', { n: routines }) : null,
    triggers ? t('adm.move.triggers', { n: triggers }) : null,
    tasks ? t('adm.move.tasks', { n: tasks }) : null,
  ];
  const out = [t('adm.move.summary', {
    dept: str(p.department?.name), from: str(p.from?.name), to: str(p.to?.name), what: listText(what),
  })];
  if (routines || triggers) out.push(t('adm.move.automations'));
  const users = (Array.isArray(p.grants?.users) ? p.grants.users : []).map(str).filter(Boolean);
  const reach = howMany(p.grants?.gained) + howMany(p.grants?.lost);
  if (users.length) out.push(t('adm.move.grants', { list: users.join(', ') }));
  else if (!reach) out.push(t('adm.move.noGrants'));
  const warnings = (Array.isArray(p.warnings) ? p.warnings : []).map((w) => str(w).trim().replace(/[.\s]+$/, '')).filter(Boolean);
  if (warnings.length) out.push(t('adm.move.warnings', { list: warnings.join(' ; ') }));
  return out.join(' ');
}

/**
 * The office once a department moved to another floor, as the server stores it: the department
 * last on its new floor, its agents with it. Shown before the bootstrap reload. Unknown ids:
 * the office unchanged.
 * @param {object} office
 * @param {string} deptId
 * @param {string} floorId
 * @returns {object} a new office
 */
export function officeAfterMove(office = {}, deptId, floorId) {
  const depts = Array.isArray(office.departments) ? office.departments : [];
  const dept = depts.find((d) => d.id === deptId);
  if (!dept || !(office.floors || []).some((f) => f.id === floorId) || dept.floorId === floorId) return office;
  const order = depts.filter((d) => d.floorId === floorId && d.id !== deptId)
    .reduce((m, d) => Math.max(m, Number.isFinite(d.order) ? d.order + 1 : 0), 0);
  return {
    ...office,
    departments: depts.map((d) => (d.id === deptId ? { ...d, floorId, order } : d)),
    agents: (office.agents || []).map((a) => (a.deptId === deptId ? { ...a, floorId } : a)),
  };
}

/**
 * Returns a copy of the office with `item` inserted/replaced (or removed) in `kind`
 * ('floors' | 'departments' | 'agents'). Used to show a change before the bootstrap reload.
 */
export function officeWith(office = {}, kind, item, { remove = false } = {}) {
  const list = Array.isArray(office[kind]) ? office[kind] : [];
  if (!item || !item.id) return office;
  const rest = list.filter((x) => x.id !== item.id);
  if (remove) return { ...office, [kind]: rest };
  const prev = list.find((x) => x.id === item.id);
  const next = prev ? list.map((x) => (x.id === item.id ? { ...prev, ...item } : x)) : [...list, item];
  return { ...office, [kind]: next };
}

/**
 * An API response unwrapped to the object it carries: `{floor: {...}}` / `{department}` /
 * `{agent}` / the bare object. Returns null when it holds no id.
 */
export function unwrapItem(res, ...keys) {
  if (!res || typeof res !== 'object') return null;
  for (const k of keys) if (res[k] && typeof res[k] === 'object' && res[k].id) return res[k];
  return res.id ? res : null;
}

/**
 * The French message of an API error, with the server's validation problems appended
 * (`details.problems`, SPEC §19 `{error, details}`), capped to a few lines.
 * @param {{message?: string, details?: {problems?: string[]}}} err
 * @returns {string}
 */
export function errorText(err) {
  const base = str(err?.message) || t('common.error');
  const problems = Array.isArray(err?.details?.problems) ? err.details.problems.map(str).filter(Boolean) : [];
  const extra = problems.filter((p) => !base.includes(p)).slice(0, 4);
  return extra.length ? `${base} — ${extra.join(' ; ')}` : base;
}

// ---------------------------------------------------------------------------------------------
// Connectors

/** Tone of a connector status for status dots (a library connector's « error » too). */
export function connectorTone(status) {
  return { connected: 'ok', 'needs-auth': 'warn', failed: 'error', error: 'error' }[status] || 'off';
}

/** {total, read, write, overridden} for a server of GET /api/connectors. */
export function toolCounts(server = {}) {
  const tools = Array.isArray(server.tools) ? server.tools : [];
  let read = 0;
  let write = 0;
  let overridden = 0;
  for (const tool of tools) {
    if (tool.kind === 'read') read += 1; else write += 1;
    if (tool.overridden) overridden += 1;
  }
  if (!tools.length) {
    return { total: Number(server.toolCount) || 0, read: Number(server.readCount) || 0, write: Number(server.writeCount) || 0, overridden: 0 };
  }
  return { total: tools.length, read, write, overridden };
}

/**
 * Names of the floors whose allowlist contains the server key (a library connector is on every
 * floor without any allowlist: the caller says so instead).
 */
export function floorsAllowing(floors, key) {
  return sortedFloors(floors || []).filter((f) => Array.isArray(f.connectors) && f.connectors.includes(key)).map((f) => f.name);
}

/** Tools whose short or full name contains `q`. */
export function filterTools(tools, q) {
  const needle = fold(q);
  const list = Array.isArray(tools) ? tools : [];
  return needle ? list.filter((x) => fold(`${x.name} ${x.full}`).includes(needle)) : list;
}

/** Servers sorted: connected first, then needs-auth, then others; by name. */
export function sortServers(servers) {
  const rank = (s) => (s.status === 'connected' ? 0 : s.status === 'needs-auth' ? 1 : 2);
  return [...(servers || [])].sort((a, b) => rank(a) - rank(b) || str(a.name || a.key).localeCompare(str(b.name || b.key), 'fr'));
}

/**
 * The engines a server of GET /api/connectors works with: its `engines` list when it has one (the
 * built-in « Tables du bureau » serve Claude and Codex agents), else its one engine.
 * @returns {Array<'claude'|'codex'>}
 */
export function serverEngines(server = {}) {
  const list = Array.isArray(server?.engines) ? server.engines.filter((e) => e === 'claude' || e === 'codex') : [];
  if (list.length) return [...new Set(list)];
  return [server?.engine === 'codex' ? 'codex' : 'claude'];
}

/** Engine label of a server card or checklist entry: "CLAUDE", "CODEX" or "CLAUDE · CODEX". */
export function serverEngineLabel(server) {
  return serverEngines(server).map((e) => t(`adm.conn.engine.${e}`)).join(' · ');
}

/** Replaces one tool (by full name) in a list of servers; returns a new list. */
export function withTool(servers, tool) {
  if (!tool || !tool.full) return servers;
  return (servers || []).map((s) => (Array.isArray(s.tools) && s.tools.some((x) => x.full === tool.full)
    ? { ...s, tools: s.tools.map((x) => (x.full === tool.full ? { ...x, kind: tool.kind, overridden: !!tool.overridden } : x)) }
    : s));
}

// ---------------------------------------------------------------------------------------------
// Engines

/** Engines shown by the Moteurs tab, in order. */
export const ENGINE_KEYS = ['claude', 'codex', 'openai-image', 'gemini', 'kie', 'mock'];

/**
 * Display state of one engine of GET /api/engines.
 * @param {string} key
 * @param {{ok?, version?, error?, enabled?, pending?, mock?, forced?}} st
 * @returns {{key: 'ok'|'error'|'disconnected'|'unchecked'|'disabled'|'pending'|'mock'|'unknown', tone: string, label: string, dot: string}}
 */
export function engineState(key, st) {
  const mk = (k, tone, dot) => ({ key: k, tone, dot, label: t(`adm.eng.state.${k}`) });
  if (!st || typeof st !== 'object') return mk('unknown', 'muted', 'off');
  if (key === 'mock') return st.forced ? mk('mock', 'amber', 'warn') : mk('ok', 'success', 'ok');
  if (st.mock) return mk('mock', 'info', 'warn');
  if (st.pending || st.state === 'pending') return mk('pending', 'info', 'off');
  if (st.enabled === false || st.state === 'disabled') return mk('disabled', 'muted', 'off');
  // Green only for an engine checked connected (account signed in, key accepted).
  if (st.state === 'disconnected') return mk('disconnected', 'danger', 'error');
  if (st.state === 'unknown') return mk('unchecked', 'amber', 'warn');
  if (st.ok && (!st.state || st.state === 'ok')) return mk('ok', 'success', 'ok');
  return mk('error', 'danger', 'error');
}
